// /managers/ConvoManager/ConvoActions.jsx
export const EMPTY_CONVO_ID = 'conv_000000';

import { getFirestore, doc, writeBatch as firestoreWriteBatch, updateDoc, setDoc, collection, query, orderBy, limit, onSnapshot, getDocs, startAfter } from 'firebase/firestore';
import { authManager } from '@/managers/AuthManager';
import { botManager } from '@/managers/BotManager';
import { keyStateManager } from '@/managers/zKeysManager';
import { getOptimalMemorySize } from './ConvoPruning';
import {
  fetchConversationMeta,
  deleteConversationFromFirebase,
  subscribeToConversationMeta,
  deleteMessagesFromFirebase,
  formatConversationForStorage,
  generateId,
  createConversationTitle,
  addMessageBatch,
  safeDecrypt,
  fetchFilesForMessages,
  fetchMessagesLazy,
  subscribeToMessageUpdates,
  formatMessageForStorage,
} from './ConvoHelpers';
import { timestampService } from '@/utils/timestampService';
import {
  DEFAULT_PROMPTED_BOT_ID,
  DEFAULT_PROMPTED_BOT_NAME,
  getBotRuntimeSystemMessage,
} from '@/utils/defaultBotProfile';
import { normalizeWorkspaceContextId, PRIVATE_WORKSPACE_PREFIX } from '@/utils/workspaceContext';
import { applyToolPresentationOverrides, isMaintenanceTool } from '@/utils/toolPresentation';
import { workspaceManager } from '@/managers/workspaceManager';
import { normalizeWorkspaceAccessSettings } from '@/utils/workspaceAccessSettings';
import { createMessageActions } from './MessageActions';
import { parseUserMessageContent, parseAssistantMessageStream, finalizeContentBlocks, updateContentBlocksIncremental } from './streamParser';
import { StreamEventTypes } from './UniversalResponseSchema';

const sanitizeForFirebase = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForFirebase(item)).filter(item => item !== undefined);
  }

  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined) {
        newObj[key] = sanitizeForFirebase(value);
      }
    }
  }
  return newObj;
};


export const createConvoActions = (state, navigate, activeMetaListenersRef, activeMessageListenersRef, lastMessageStatesRef) => {
  const firestore = getFirestore();
  const MAX_MESSAGES_IN_STATE = 50;

  const _normalizeAndParseEvents = (events) => {
    if (!Array.isArray(events)) return [];
    
    const normalizedEvents = events.map(event => {
      if (event && typeof event.type === 'string') {
        return { ...event, type: event.type.toUpperCase() };
      }
      return event;
    });

    const result = parseAssistantMessageStream(normalizedEvents);
    return result;
  };


  const getMessageContentBlocks = (role, decryptedContentForUI, storedRawContent, hasAudio = false, transcript = null, thoughtSummary = null) => {
    const prependThinkingBlockIfNeeded = (blocks) => {
      if (role !== 'assistant' || !thoughtSummary || !thoughtSummary.trim()) {
        return blocks;
      }

      if (!Array.isArray(blocks)) {
        return blocks;
      }

      const hasThinkingBlock = blocks.some((block) => block?.type === 'thinking');
      if (hasThinkingBlock) {
        return blocks;
      }

      return [{
        type: 'thinking',
        id: `thinking_${generateId()}`,
        content: thoughtSummary.trim(),
        isFinal: true,
      }, ...blocks];
    };

    // CRITICAL FIX: For user audio messages, ALWAYS build blocks from rawContent + transcript
    // Skip the contentForUI parsing because it may contain stale data from initial save
    if (role === 'user' && hasAudio) {
        const blocks = [];
        
        const hasTypedContent = storedRawContent && typeof storedRawContent === 'string' && storedRawContent.trim() !== '';
        if (hasTypedContent) {
            blocks.push({ 
                type: 'text', 
                id: `text_${generateId()}`, 
                content: storedRawContent 
            });
        }
        
        const transcriptValue = transcript || '';
        const hasTranscriptContent = transcriptValue.length > 0 && transcriptValue.trim() !== '';
        
        if (hasTranscriptContent) {
            blocks.push({ 
                type: 'transcription', 
                id: `transcript_${generateId()}`, 
                content: transcriptValue
            });
        } else if (!hasTypedContent) {
            blocks.push({ 
                type: 'transcribing', 
                id: `transcribing_${generateId()}`, 
                content: 'Transcribing...' 
            });
        }
        
        if (blocks.length > 0) {
            return blocks;
        }
        
        return [{ type: 'text', id: `empty_audio_${generateId()}`, content: '[Audio message]' }];
    }

    // For non-audio messages, try to parse structured content from contentForUI
    let structuredData;
    if (typeof decryptedContentForUI === 'string' && decryptedContentForUI.trim().startsWith('[')) {
        try { structuredData = JSON.parse(decryptedContentForUI); } catch (e) { /* ignore */ }
    } else if (Array.isArray(decryptedContentForUI)) {
        structuredData = decryptedContentForUI;
    }

    if (Array.isArray(structuredData) && structuredData.length > 0) {
        const firstItem = structuredData[0];
        
        const isEventStream = firstItem && typeof firstItem.type === 'string' && firstItem.payload !== undefined;
        const isFinalBlockArray = firstItem && typeof firstItem.type === 'string' && firstItem.payload === undefined;
        
        if (isEventStream) {
          return prependThinkingBlockIfNeeded(_normalizeAndParseEvents(structuredData));
        } else if (isFinalBlockArray) {
          return prependThinkingBlockIfNeeded(structuredData);
        }
    }
    
    if (role === 'user') {
        return parseUserMessageContent(storedRawContent);
    }

    // Handle assistant message reconstruction from persisted fields
    if (role === 'assistant') {
        const blocks = [];
        
        // Prepend thinking block if we have thought summary
        if (thoughtSummary && thoughtSummary.trim()) {
            blocks.push({ 
                type: 'thinking', 
                id: `thinking_${generateId()}`, 
                content: thoughtSummary.trim(),
                isFinal: true 
            });
        }
        
        // Add text content
        if (storedRawContent) {
            blocks.push({ 
                type: 'text', 
                id: `text_${generateId()}`, 
                content: storedRawContent,
                isFinal: true 
            });
        }
        
        if (blocks.length > 0) return blocks;
    }

    // Fallback for any other cases
    if (storedRawContent) {
        return [{ type: 'text', id: `fallback_${generateId()}`, content: storedRawContent }];
    }

    return [];
};

  const cleanupMetaListener = (convoId) => {
    if (activeMetaListenersRef.current[convoId]) {
      activeMetaListenersRef.current[convoId]();
      delete activeMetaListenersRef.current[convoId];
    }
  };

  const setupMetaListenerInternal = (convoId) => {
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid || activeMetaListenersRef.current[convoId]) {
      return; 
    }
    const unsubscribe = subscribeToConversationMeta(
      firestore, currentAuthUser.uid, convoId,
      (conversationData) => {
        if (!conversationData) { 
          state.updateConversationsMeta(prev => {
            const next = { ...prev };
            delete next[convoId];
            return next;
          });
          cleanupMetaListener(convoId); 
          if (state.currentConversationId === convoId) {
            state.setCurrentConversationId(null);
            navigate('/'); 
          }
          return;
        }
        const existingMeta = state.conversationsMeta[convoId] || {};
        state.setConversationMeta(convoId, { ...existingMeta, ...conversationData });
      }
    );
    activeMetaListenersRef.current[convoId] = unsubscribe;
  };
  
  const cleanupMessageListener = (convoId) => {
    if (activeMessageListenersRef.current[convoId]) {
      activeMessageListenersRef.current[convoId]();
      delete activeMessageListenersRef.current[convoId];
    }
    lastMessageStatesRef.current.delete(convoId);
  };

  const setupMessageListener = (convoId) => {
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid || activeMessageListenersRef.current[convoId]) {
      return;
    }
    
    const handleUpdate = async (update) => {
      const { messageId, data } = update;
      
      const convoStates = lastMessageStatesRef.current.get(convoId) || new Map();
      
      const currentMessages = state.messages[convoId] || [];
      const existingMessage = currentMessages.find(m => m.id === messageId);

      const decryptedContentString = await safeDecrypt(data.contentForUI, currentAuthUser.uid);
      const decryptedThoughts = await safeDecrypt(data.thoughtSummary, currentAuthUser.uid);
      const storedRawContent = await safeDecrypt(data.rawContent, currentAuthUser.uid);
      const decryptedTranscript = await safeDecrypt(data.transcript, currentAuthUser.uid);
      // If Firestore fires before server persists thoughtSummary, fall back to the
      // in-memory value that was captured during the live stream.
      const effectiveThoughts = decryptedThoughts || existingMessage?.thoughtSummary || '';
      
      const newSignature = {
        contentForUI: decryptedContentString,
        status: data.status,
        thoughtSummary: effectiveThoughts,
        transcript: decryptedTranscript,
        transcriptionStatus: data.transcriptionStatus,
        transcriptionProcessed: data.transcriptionProcessed,
      };

      const lastSignature = convoStates.get(messageId);
      
      const hasSignificantChange = existingMessage?.isOptimistic || !lastSignature || 
                                   lastSignature.status !== newSignature.status ||
                                   lastSignature.contentForUI !== newSignature.contentForUI ||
                                   lastSignature.thoughtSummary !== newSignature.thoughtSummary ||
                                   lastSignature.transcript !== newSignature.transcript ||
                                   lastSignature.transcriptionStatus !== newSignature.transcriptionStatus ||
                                   lastSignature.transcriptionProcessed !== newSignature.transcriptionProcessed;

      if (!hasSignificantChange) {
        return;
      }

      convoStates.set(messageId, newSignature);
      lastMessageStatesRef.current.set(convoId, convoStates);
      
      const finalContentBlocks = getMessageContentBlocks(
        data.role, 
        decryptedContentString, 
        storedRawContent, 
        data.hasAudio, 
        decryptedTranscript,
        effectiveThoughts
      );
      
      let decryptedMemories = [];
      if (data.retrievedMemories && Array.isArray(data.retrievedMemories)) {
        decryptedMemories = await Promise.all(
          data.retrievedMemories.map(async (mem) => {
            if (typeof mem === 'object' && mem.content) {
              return { ...mem, content: await safeDecrypt(mem.content, currentAuthUser.uid) };
            }
            return mem;
          })
        );
      }

      const finalUpdatePayload = {
        ...data,
        rawContent: storedRawContent,
        transcript: decryptedTranscript,
        streamEvents: (typeof decryptedContentString === 'string' && decryptedContentString.trim().startsWith('[')) ? JSON.parse(decryptedContentString) : (Array.isArray(decryptedContentString) ? decryptedContentString : []),
        contentForUI: undefined, 
        contentBlocks: finalContentBlocks,
        thoughtSummary: effectiveThoughts,
        retrievedMemories: decryptedMemories,
        files: existingMessage?.files || [],
        images: existingMessage?.images || [],
        metadata: {
          ...(data.metadata || {}),
        },
        _pendingDecryption: false,
        isOptimistic: false,
        _isFinalized: true, 
      };
      
      state.updateOptimisticMessage(convoId, messageId, finalUpdatePayload);
    };

    const unsubscribe = subscribeToMessageUpdates(firestore, currentAuthUser.uid, convoId, handleUpdate);
    activeMessageListenersRef.current[convoId] = unsubscribe;
  };

  const createConversation = async (options = {}) => {
    const currentAuthSnapshot = authManager.getState();
    const currentAuthUser = currentAuthSnapshot.user;
    if (!currentAuthUser?.uid) return { success: false, error: 'User not authenticated', id: null };

    const resolveWorkspaceContextId = async () => {
      const requestedWorkspaceId = normalizeWorkspaceContextId(options.workspaceContextId);
      if (requestedWorkspaceId) {
        return requestedWorkspaceId;
      }

      const getWorkspaceIdFromState = () => {
        const workspaceState = workspaceManager.getState();
        return workspaceState.activeWorkspaceDetail?.id
          || workspaceState.workspaces.find((workspace) => workspace.isDefault)?.id
          || workspaceState.activeWorkspaceId
          || null;
      };

      let resolvedWorkspaceId = getWorkspaceIdFromState();
      if (resolvedWorkspaceId) {
        return resolvedWorkspaceId;
      }

      if (typeof workspaceManager.refreshNow === 'function') {
        await workspaceManager.refreshNow().catch(() => null);
        resolvedWorkspaceId = getWorkspaceIdFromState();
      }

      return resolvedWorkspaceId;
    };
    
    const isIncognitoMode = options.incognitoMode === true || options.privacyMode === 'incognito';
    const { fromInitNoNav = false, ...botOptions } = options;
    if (!fromInitNoNav && state.isNavigating) return { success: false, error: 'Navigation in progress', id: null };
    if (!fromInitNoNav) state.setIsNavigating(true);

    try {
      const resolvedWorkspaceContextId = await resolveWorkspaceContextId();
      const botManagerState = botManager.getState();
      let botData = botOptions?.botId ? botManagerState.allBots.find(b => b.id === botOptions.botId) : botManagerState.allBots.find(b => b.id === botManagerState.activeBotConfig?.botId);
      let modelData = botOptions?.modelId ? botManagerState.allPlatformModels.find(m => m.id === botOptions.modelId) : botManagerState.allPlatformModels.find(m => m.id === botManagerState.activeBotConfig?.modelId);
      const keyState = keyStateManager.getState();
      const hasKeys = keyState.isKeysReady && keyState.hasKeys;

      if ((!botData || !modelData) && hasKeys) {
        const defaultBotInfo = botManager.findDefaultBot();
        if (defaultBotInfo?.bot && defaultBotInfo?.variation) {
          botData = defaultBotInfo.bot;
          modelData = defaultBotInfo.variation;
        }
      }
      
      if (!botData || !modelData) {
        if (hasKeys) {
          if (!fromInitNoNav) {
            state.setIsNavigating(false);
          }
          return { success: false, error: 'Keys are present but no usable bot/model is available.', id: null };
        }

        // No bot/model (new user without API keys).
        // Seat the well-known ephemeral placeholder — never persisted, always re-usable.
        if (!state.conversationsMeta[EMPTY_CONVO_ID]) {
          const emptyMeta = {
            id: EMPTY_CONVO_ID, userId: currentAuthUser.uid, status: 'new',
            persisted: false, isEmpty: true,
            botId: null, modelId: null, botName: null, modelName: null,
            botPlatform: null, botModel: null, botCleanModelName: null,
            name: '', messageCount: 0, lastMessagePreview: null,
            toolSettings: { activeToolIds: [] }, mcpSettings: { activeServerIds: [] },
            thinkingSettings: { enabled: false, budget: 0 },
            messagesLoaded: true, hasMoreMessages: false,
            isLoadingMessages: false, errorLoadingMessages: null,
            workspaceContextId: isIncognitoMode ? `${PRIVATE_WORKSPACE_PREFIX}${EMPTY_CONVO_ID}` : resolvedWorkspaceContextId,
            privacyMode: isIncognitoMode ? 'incognito' : 'normal',
            incognitoMeta: isIncognitoMode ? { enabledAt: new Date().toISOString() } : null,
            parManifest: null, pendingMessage: null,
          };
          state.setConversationMeta(EMPTY_CONVO_ID, emptyMeta);
          state.setMessages(EMPTY_CONVO_ID, []);
        }
        state.setCurrentConversationId(EMPTY_CONVO_ID);
        state.setError(null);
        if (!fromInitNoNav) {
          navigate(`/chat/${EMPTY_CONVO_ID}`, { replace: true });
          state.setIsNavigating(false);
        }
        return { success: true, conversation: state.conversationsMeta[EMPTY_CONVO_ID], id: EMPTY_CONVO_ID };
      }

      const conversationId = generateId('conv');
      const userTier = currentAuthSnapshot.tier || 'Free';
      const initialAIContextSize = getOptimalMemorySize([], userTier);
      const clientSideCreatedTimestamp = await timestampService.getTimestamp();
      const normalizedWorkspaceContextId = isIncognitoMode
        ? `${PRIVATE_WORKSPACE_PREFIX}${conversationId}`
        : resolvedWorkspaceContextId;

      const { globalTools = [], mcpServers = [] } = authManager.getState();
      
      const normalizedGlobalTools = globalTools.map((tool) => applyToolPresentationOverrides(tool));

      const defaultToolIds = normalizedGlobalTools
        .filter(tool => {
            if (tool.maintenanceMode || isMaintenanceTool(tool)) return false;
            const isActiveByDefault = tool.activeByDefault === undefined ? true : tool.activeByDefault;
            if (!isActiveByDefault) return false;

            const tiers = Array.isArray(tool.tiers) ? tool.tiers : [];
            const hasTierAccess = tiers.length === 0 || tiers.some((tier) => authManager.hasTierAccess(tier));
            const supportedPlatforms = Array.isArray(tool.platforms) && tool.platforms.length > 0
              ? tool.platforms
              : (tool.platform ? [tool.platform] : []);
            const isPlatformCompatible = supportedPlatforms.length === 0 || supportedPlatforms.includes(modelData.platform);

            return hasTierAccess && isPlatformCompatible;
        })
        .map(tool => tool.nameToPass);

      const defaultMcpIds = mcpServers
        .filter(server => server.enabled === undefined ? true : !!server.enabled)
        .map(server => server.id);

      const workspaceState = workspaceManager.getState();
      const scopedWorkspace = normalizedWorkspaceContextId
        ? (workspaceState.activeWorkspaceDetail?.id === normalizedWorkspaceContextId
            ? workspaceState.activeWorkspaceDetail
            : workspaceState.workspaces.find(workspace => workspace.id === normalizedWorkspaceContextId))
        : null;
      const workspaceAccessSettings = normalizeWorkspaceAccessSettings(scopedWorkspace?.workspaceAccessSettings);
      const initialToolIds = workspaceAccessSettings.activeToolIds ?? defaultToolIds;
      const initialMcpIds = workspaceAccessSettings.activeServerIds ?? defaultMcpIds;

      const userThinkingPrefs = authManager.getThinkingPreferences(botData.id, modelData.id);
      const hasThinkingCapability = modelData.hasThinking || modelData.platform?.toUpperCase() === 'CLAUDE';
      const thinkingSettings = {
        enabled: userThinkingPrefs?.enabled ?? (hasThinkingCapability ? true : false),
        budget: userThinkingPrefs?.budget ?? 1800
      };

      const newConversationMeta = {
        id: conversationId, userId: currentAuthUser.uid, botId: botData.id, botName: botData.name,
        modelId: modelData.id, modelName: modelData.name, botPlatform: modelData.platform,
        botModel: modelData.model, botCleanModelName: modelData.cleanModelName || modelData.model,
        modelMaxTokens: modelData.maxTokens || null,
        temperature: modelData.temperatureRange ? ((modelData.temperatureRange[0] === 0 && modelData.temperatureRange[1] === 1) || (modelData.temperatureRange[0] === 0 && modelData.temperatureRange[1] === 2) ? 0.4 : (modelData.temperatureRange[0] + modelData.temperatureRange[1]) / 2) : 0.4,
        customSystemMessage: getBotRuntimeSystemMessage(botData),
        name: `Chat with ${botData.name}`, status: 'new', persisted: false, 
        clientSideCreated: clientSideCreatedTimestamp, created: clientSideCreatedTimestamp, 
        memorySize: initialAIContextSize,
        toolSettings: { activeToolIds: initialToolIds },
        mcpSettings: { activeServerIds: initialMcpIds },
        thinkingSettings,
        messageCount: 0, lastMessagePreview: null,
        workspaceContextId: normalizedWorkspaceContextId,
        parManifest: null,
        pendingMessage: botOptions?.pendingMessage || null,
        hasMoreMessages: false, oldestLoadedMessageTimestamp: null,
        messagesLoaded: true, isLoadingMessages: false, errorLoadingMessages: null,
        privacyMode: isIncognitoMode ? 'incognito' : 'normal',
        incognitoMeta: isIncognitoMode ? { enabledAt: new Date().toISOString() } : null,
      };

      state.setConversationMeta(conversationId, newConversationMeta);
      state.setMessages(conversationId, []); 
      state.setCurrentConversationId(conversationId);
      state.setError(null);

      if (!fromInitNoNav) {
        navigate(`/chat/${conversationId}`, { replace: true });
        state.setIsNavigating(false);
      }
      
      return { success: true, conversation: newConversationMeta, id: conversationId };
    } catch (err) {
      state.setError(err.message);
      if (!fromInitNoNav) state.setIsNavigating(false);
      return { success: false, error: err.message, id: null };
    }
  };

  const updateConversation = async (conversationId, updates) => {
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid) return false;

    const currentMeta = state.conversationsMeta[conversationId];
    if (!currentMeta) return false;

    const finalUpdates = typeof updates === 'function' ? updates(currentMeta) : updates;

    if ('workspaceContextId' in finalUpdates) {
      finalUpdates.workspaceContextId = normalizeWorkspaceContextId(finalUpdates.workspaceContextId);
    }

    const shouldPersistNow = !currentMeta.persisted && 'workspaceContextId' in finalUpdates;

    const optimisticUpdates = { ...finalUpdates };
    if (shouldPersistNow) {
        optimisticUpdates.persisted = true;
    }
    state.setConversationMeta(conversationId, optimisticUpdates);

    if (currentMeta.persisted || shouldPersistNow) {
        try {
            const convoRef = doc(firestore, 'users', currentAuthUser.uid, 'conversations', conversationId);
            const payloadForStorage = { ...finalUpdates };

            if (shouldPersistNow) {
                payloadForStorage.persisted = true;
                if (!currentMeta.created) {
                    const creationTimestamp = await timestampService.getTimestamp();
                    payloadForStorage.created = creationTimestamp;
                    state.setConversationMeta(conversationId, { created: creationTimestamp });
                }
            }

            const finalPayload = formatConversationForStorage(payloadForStorage);
            await setDoc(convoRef, finalPayload, { merge: true });

        } catch (err) {
            state.setConversationMeta(conversationId, currentMeta);
            throw err;
        }
    }
    return true;
  };
  
  const updateConversationBotAndModel = async (conversationId, botData, modelData) => {
    const updates = {
      botId: botData.id, botName: botData.name, modelId: modelData.id,
      modelName: modelData.name || modelData.model, botPlatform: modelData.platform,
      botModel: modelData.model, botCleanModelName: modelData.cleanModelName || modelData.model,
      modelMaxTokens: modelData.maxTokens || null,
      temperature: modelData.temperatureRange ? (modelData.temperatureRange[0] + modelData.temperatureRange[1]) / 2 : 0.7,
      customSystemMessage: botData.customSystemMessage || null,
    };
    return updateConversation(conversationId, updates);
  };

  const sendUserMessage = async (messagePayload) => {
    const currentAuthUser = authManager.getCurrentUser();
    const conversationId = state.currentConversationId;
    if (!currentAuthUser?.uid || !conversationId) return { success: false, error: "No active conversation or user." };
    if (state.isNavigating) return { success: false, error: "Navigation in progress, please wait." };
    
    const currentConvoMeta = state.conversationsMeta[conversationId];
    if (!currentConvoMeta) return { success: false, error: `Conversation ${conversationId} not found.` };

    const { id: localUserMessageId, rawContent, files = {}, images = [], hasAudio, timestamp: userMessageTimestamp, contentBlocks } = messagePayload;
    
    let finalContentBlocksForUser;
    // For audio messages without typed text, show transcribing state
    if (hasAudio && (!rawContent || rawContent.trim() === '')) {
        finalContentBlocksForUser = [{ type: 'transcribing', id: `transcribing_${localUserMessageId}`, content: 'Transcribing...' }];
    } else if (hasAudio && rawContent && rawContent.trim() !== '') {
        // Has both audio and typed text - show text now, transcript will be added via listener
        finalContentBlocksForUser = [
            { type: 'text', id: `text_${localUserMessageId}`, content: rawContent },
            { type: 'transcribing', id: `transcribing_${localUserMessageId}`, content: 'Transcribing...' }
        ];
    } else {
        finalContentBlocksForUser = (contentBlocks && contentBlocks.length > 0)
          ? contentBlocks
          : parseUserMessageContent(rawContent);
    }

    const optimisticUserMessage = {
      id: localUserMessageId, role: 'user', rawContent, 
      streamEvents: [], 
      contentBlocks: finalContentBlocksForUser, 
      files: Object.values(files || {}), 
      images: images || [],
      hasAudio: hasAudio || false,
      transcript: null,
      timestamp: userMessageTimestamp, status: 'sending', isOptimistic: true, persisted: false,
      userId: currentAuthUser.uid, botId: currentConvoMeta.botId, modelId: currentConvoMeta.modelId,
    };
    state.addOptimisticMessage(conversationId, optimisticUserMessage); 

    let convoName = currentConvoMeta.name;
    if ((convoName === `Chat with ${currentConvoMeta.botName}` || convoName === 'New Conversation') && rawContent) {
      convoName = createConversationTitle(rawContent);
    }

    const metaUpdatesForSend = {
      status: 'inflight', messageCount: (currentConvoMeta.messageCount || 0) + 1,
      lastMessagePreview: { id: optimisticUserMessage.id, role: 'user', timestamp: userMessageTimestamp, status: 'sending' },
      name: convoName,
    };
    state.setConversationMeta(conversationId, metaUpdatesForSend);

    try {
      const batch = firestoreWriteBatch(firestore);
      const convoRef = doc(firestore, 'users', currentAuthUser.uid, 'conversations', conversationId);
      
      const convoUpdatePayload = { 
        name: convoName, status: 'inflight', 
        messageCount: (currentConvoMeta.messageCount || 0) + 1, 
        lastMessagePreview: { id: optimisticUserMessage.id, role: 'user', timestamp: userMessageTimestamp, status: 'sent' } 
      };
      
      const createdTimestamp = currentConvoMeta.persisted ? currentConvoMeta.created : (currentConvoMeta.clientSideCreated || userMessageTimestamp);
      const fullConvoDataForStorage = formatConversationForStorage({ ...currentConvoMeta, ...metaUpdatesForSend, ...convoUpdatePayload, persisted: true, created: createdTimestamp });
      batch.set(convoRef, fullConvoDataForStorage, { merge: true });

      const messageDataForFirebase = {
        id: optimisticUserMessage.id, role: 'user', rawContent: optimisticUserMessage.rawContent,
        hasAudio: optimisticUserMessage.hasAudio,
        status: 'sent', userId: currentAuthUser.uid, botId: currentConvoMeta.botId,
        modelId: currentConvoMeta.modelId, botName: currentConvoMeta.botName,
        modelName: currentConvoMeta.modelName, timestamp: userMessageTimestamp,
      };

      await addMessageBatch(firestore, currentAuthUser.uid, conversationId, messageDataForFirebase, { files: Object.values(files || {}), images: images || [] }, batch);
      
      await batch.commit(); 
      state.updateOptimisticMessage(conversationId, localUserMessageId, { status: 'sent', persisted: true, isOptimistic: false });
      
      if (!currentConvoMeta.persisted) {
        setupMessageListener(conversationId);
      }
      
      return { success: true, conversationId };
    } catch (err) {
      state.setConversationMeta(conversationId, {
        status: currentConvoMeta.status || 'error', 
        messageCount: currentConvoMeta.messageCount || 0,
        lastMessagePreview: currentConvoMeta.lastMessagePreview || null, 
      });
      state.updateOptimisticMessage(conversationId, localUserMessageId, { status: 'error', errorDetails: { message: err.message, code: 'SEND_FAILED' } });
      return { success: false, error: err.message };
    }
  };

  const switchToConversation = async (conversationId, options = {}) => {
    // The empty placeholder is not in Firestore — re-seat it via createConversation instead.
    if (conversationId === EMPTY_CONVO_ID) {
      return createConversation({ fromInitNoNav: !!options.newTab });
    }
    const fallbackToNewOnMissing = options.fallbackToNewOnMissing !== false;
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid) return { success: false, error: 'User not authenticated' };
    if (!options.newTab && state.isNavigating && state.currentConversationId !== conversationId) {
      return { success: false, error: 'Navigation in progress' };
    }
  
    if (!options.newTab) state.setIsNavigating(true);
  
    const handleNotFound = async () => {
      if (!fallbackToNewOnMissing) {
        state.setError('Conversation not found.');
        return { success: false, error: 'Conversation not found.' };
      }

      if (!options.newTab) {
        const { success, id: newId } = await createConversation({ fromInitNoNav: true });
        if (success && newId) {
          navigate(`/chat/${newId}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
      return { success: false, error: "Conversation not found; new one created." };
    };
  
    try {
      state.setCurrentConversationId(conversationId);
      state.setError(null);
  
      let targetMeta = state.conversationsMeta[conversationId];
  
      if (!targetMeta) {
        targetMeta = await fetchConversationMeta(firestore, currentAuthUser.uid, conversationId);
        if (!targetMeta) {
          return await handleNotFound();
        }
        state.setConversationMeta(conversationId, targetMeta);
      }
  
      const needsMessageLoad = targetMeta.persisted && !targetMeta.messagesLoaded;

      if (needsMessageLoad) {
        state.setConversationMeta(conversationId, { isLoadingMessages: true });
        const initialLoadCount = targetMeta.memorySize || 20;
        const { messages: fetchedMessages } = await fetchMessagesLazy(firestore, currentAuthUser.uid, conversationId, {
          limit: Math.min(initialLoadCount, MAX_MESSAGES_IN_STATE),
          reverse: true,
        });
  
        const messageIds = fetchedMessages.map(m => m.id);
        const attachmentsByMessageId = await fetchFilesForMessages(firestore, currentAuthUser.uid, conversationId, messageIds);
  
        const messagesForUI = await Promise.all(fetchedMessages.map(async (msg) => {
          const attachments = attachmentsByMessageId[msg.id] || { files: [], images: [] };
          
          const [decryptedContentForUI, storedRawContent, decryptedTranscript, decryptedMemories, decryptedThoughts] = await Promise.all([
              safeDecrypt(msg.contentForUI, currentAuthUser.uid),
              safeDecrypt(msg.rawContent, currentAuthUser.uid),
              safeDecrypt(msg.transcript, currentAuthUser.uid),
              (async () => {
                if (!Array.isArray(msg.retrievedMemories)) return [];
                return await Promise.all(
                  msg.retrievedMemories.map(async (mem) => ({
                    ...mem,
                    content: await safeDecrypt(mem.content, currentAuthUser.uid),
                  }))
                );
              })(),
              safeDecrypt(msg.thoughtSummary, currentAuthUser.uid)
          ]);

          const finalContentBlocks = getMessageContentBlocks(
            msg.role, 
            decryptedContentForUI, 
            storedRawContent, 
            msg.hasAudio, 
            decryptedTranscript,
            decryptedThoughts
          );
          
          return {
            ...msg,
            files: attachments.files || [],
            images: attachments.images || [],
            rawContent: storedRawContent,
            transcript: decryptedTranscript,
            streamEvents: (typeof decryptedContentForUI === 'string' && decryptedContentForUI.trim().startsWith('[')) ? JSON.parse(decryptedContentForUI) : (Array.isArray(decryptedContentForUI) ? decryptedContentForUI : []),
            contentBlocks: finalContentBlocks,
            thoughtSummary: decryptedThoughts,
            retrievedMemories: decryptedMemories,
            _pendingDecryption: false,
          };
        }));
        
        state.setMessages(conversationId, messagesForUI);
        
        state.setConversationMeta(conversationId, {
          messagesLoaded: true,
          isLoadingMessages: false,
          hasMoreMessages: fetchedMessages.length >= Math.min(initialLoadCount, MAX_MESSAGES_IN_STATE),
          oldestLoadedMessageTimestamp: fetchedMessages.length > 0 ? fetchedMessages[0].timestamp : null,
        });
      }
  
      const finalMeta = state.conversationsMeta[conversationId];
      if (finalMeta?.botId && finalMeta?.modelId) {
        botManager.setCurrentBotAndModel(finalMeta.botId, finalMeta.modelId);
      }
  
      const url = `/chat/${conversationId}`;
      if (options.newTab) {
        window.open(url, '_blank');
      } else if (window.location.pathname !== url) {
        navigate(url, { replace: true });
      }
  
      return { success: true };
    } catch (error) {
      state.setConversationMeta(conversationId, { isLoadingMessages: false, errorLoadingMessages: error.message, persisted: false });
      return await handleNotFound();
    } finally {
      if (!options.newTab) state.setIsNavigating(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid) return false;
    
    const metaBeforeDelete = state.conversationsMeta[conversationId]
      ? { ...state.conversationsMeta[conversationId] }
      : null;
      
    state.updateConversationsMeta(prev => {
      const next = { ...prev };
      delete next[conversationId];
      return next;
    });
    cleanupMetaListener(conversationId); 
    cleanupMessageListener(conversationId);

    try {
      if (metaBeforeDelete?.persisted || (await fetchConversationMeta(firestore, currentAuthUser.uid, conversationId))) {
        await deleteConversationFromFirebase(firestore, currentAuthUser.uid, conversationId);
      }
      if (state.currentConversationId === conversationId) {
        state.setCurrentConversationId(null);
        const { success, id: newId } = await createConversation({ fromInitNoNav: true }); 
        if (success && newId) navigate(`/chat/${newId}`, { replace: true });
        else navigate('/', { replace: true });
      }
      return true;
    } catch (error) {
      if (metaBeforeDelete) {
        state.setConversationMeta(conversationId, metaBeforeDelete);
        if (metaBeforeDelete.persisted) {
          setupMetaListenerInternal(conversationId);
        }
      }
      return false;
    }
  };
  
  const addOptimisticAssistantMessage = async (conversationId, assistantMessageId, initialData = {}) => {
    const currentConvoMeta = state.conversationsMeta[conversationId];
      const providedTimestamp = initialData.timestamp;
      const baseTimestamp = providedTimestamp instanceof Date
        ? providedTimestamp
        : await timestampService.getTimestamp();
      const assistantTimestamp = providedTimestamp instanceof Date
        ? providedTimestamp
        : new Date(baseTimestamp.getTime() + 1);
    
    const allowThinking = initialData._allowThinking;
    delete initialData._allowThinking;

    const initialContentBlocks = allowThinking ? [{
      type: 'thinking',
      id: `thinking_placeholder_${assistantMessageId}`,
      content: '',
    }] : [];

    const messageData = {
      id: assistantMessageId,
      role: 'assistant',
      rawContent: '',
      streamEvents: [],
      contentBlocks: initialContentBlocks,
      status: 'inflight',
      isOptimistic: true,
      persisted: false,
      toolStatus: 'idle',
      timestamp: assistantTimestamp,
      userId: currentConvoMeta?.userId || authManager.getCurrentUser()?.uid,
      botId: currentConvoMeta?.botId,
      modelId: currentConvoMeta?.modelId,
      ...initialData,
      metadata: {
        ...(initialData.metadata || {}),
        allowThinking: !!allowThinking,
      }
    };
    
    state.addOptimisticMessage(conversationId, messageData);
  };

  const updateOptimisticAssistantMessage = (conversationId, messageId, updates) => {
    const finalUpdates = { ...updates };
    if (updates.status === 'delivered' || updates.status === 'error' || updates.status === 'cancelled' ) { 
        finalUpdates.isOptimistic = false;
        finalUpdates.contentForUI = undefined;
    }
    state.updateOptimisticMessage(conversationId, messageId, finalUpdates);
  };

  const loadMoreMessagesIfNeeded = async (conversationId, requiredCount) => {
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid) return;
    
    const convoMeta = state.conversationsMeta[conversationId];
    const messages = state.messages[conversationId] || [];
    const messagesCurrentlyLoaded = messages.length;
    
    if (
      !convoMeta ||
      convoMeta.isLoadingMoreMessages ||
      !convoMeta.hasMoreMessages ||
      requiredCount <= messagesCurrentlyLoaded
    ) {
      return;
    }

    const messagesToFetch = Math.max(0, requiredCount - messagesCurrentlyLoaded);

    if (messagesToFetch === 0) {
      state.setConversationMeta(conversationId, { isLoadingMoreMessages: false });
      return;
    }

    state.setConversationMeta(conversationId, { isLoadingMoreMessages: true, errorLoadingMessages: null });
    const oldestTimestamp = messagesCurrentlyLoaded > 0 ? messages[0].timestamp : convoMeta.oldestLoadedMessageTimestamp;

    try {
      const queryOptions = {
        limit: messagesToFetch,
        reverse: true,
      };
      if (oldestTimestamp) {
        queryOptions.startAfter = oldestTimestamp;
      }

      const { messages: olderMessages } = await fetchMessagesLazy(
        firestore, currentAuthUser.uid, conversationId, queryOptions
      );

      if (olderMessages.length === 0) {
        state.setConversationMeta(conversationId, { hasMoreMessages: false, isLoadingMoreMessages: false });
        return;
      }

      const messageIds = olderMessages.map(m => m.id);
      const attachmentsByMessageId = await fetchFilesForMessages(firestore, currentAuthUser.uid, conversationId, messageIds);
      
      const messagesForUI = await Promise.all(olderMessages.map(async (msg) => {
        const attachments = attachmentsByMessageId[msg.id] || { files: [], images: [] };
        const decryptedContentForUI = await safeDecrypt(msg.contentForUI, currentAuthUser.uid);
        const storedRawContent = await safeDecrypt(msg.rawContent, currentAuthUser.uid);
        const decryptedTranscript = await safeDecrypt(msg.transcript, currentAuthUser.uid);
        const decryptedThoughts = await safeDecrypt(msg.thoughtSummary, currentAuthUser.uid);
        
        const finalContentBlocks = getMessageContentBlocks(
          msg.role, 
          decryptedContentForUI, 
          storedRawContent, 
          msg.hasAudio, 
          decryptedTranscript,
          decryptedThoughts
        );

        const decryptedMemories = Array.isArray(msg.retrievedMemories) 
          ? await Promise.all(msg.retrievedMemories.map(async mem => ({...mem, content: await safeDecrypt(mem.content, currentAuthUser.uid)})))
          : [];

        const messageForState = {
          ...msg,
          files: attachments.files || [],
          images: attachments.images || [],
          rawContent: storedRawContent,
          transcript: decryptedTranscript,
          streamEvents: (typeof decryptedContentForUI === 'string' && decryptedContentForUI.trim().startsWith('[')) ? JSON.parse(decryptedContentForUI) : (Array.isArray(decryptedContentForUI) ? decryptedContentForUI : []),
          contentBlocks: finalContentBlocks,
          thoughtSummary: decryptedThoughts,
          retrievedMemories: decryptedMemories,
          metadata: {
            ...(msg.metadata || {}),
          },
          _pendingDecryption: false,
        };
        return messageForState;
      }));
      
      if (messagesCurrentlyLoaded > 0) {
        state.prependMessages(conversationId, messagesForUI);
      } else {
        state.setMessages(conversationId, messagesForUI);
      }
      
      state.setConversationMeta(conversationId, {
        isLoadingMoreMessages: false,
        hasMoreMessages: olderMessages.length === messagesToFetch,
        oldestLoadedMessageTimestamp: olderMessages.length > 0 ? messagesForUI[0].timestamp : oldestTimestamp,
      });

    } catch (error) {
      state.setConversationMeta(conversationId, { isLoadingMoreMessages: false, errorLoadingMessages: error.message });
    }
  };
  
  const updateAIMemorySize = async (size) => {
    if (state.currentConversationId) {
      return updateConversation(state.currentConversationId, { memorySize: size });
    }
    return false;
  };

  const updateTemperature = async (temp) => {
    if (state.currentConversationId) {
      return updateConversation(state.currentConversationId, { temperature: temp });
    }
    return false;
  };

  const updateMessage = async (conversationId, messageId, updates) => {
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid || !conversationId || !messageId) {
        return { success: false, error: "Missing required IDs for message update." };
    }

    try {
        const messageRef = doc(firestore, 'users', currentAuthUser.uid, 'conversations', conversationId, 'messages', messageId);

        const existingMessage = (state.messages[conversationId] || []).find(m => m.id === messageId);
        if (!existingMessage) {
            return { success: false, error: "Original message not found in state." };
        }
        
        const updatedMessageObject = { ...existingMessage, ...updates };

        const payloadForStorage = await formatMessageForStorage(updatedMessageObject, currentAuthUser.uid);
        
        const finalDbPayload = {};

        if ('rawContent' in updates) finalDbPayload.rawContent = payloadForStorage.rawContent;
        if ('contentBlocks' in updates || 'streamEvents' in updates) finalDbPayload.contentForUI = payloadForStorage.contentForUI;
        if ('metadata' in updates) finalDbPayload.metadata = payloadForStorage.metadata;
        if ('status' in updates) finalDbPayload.status = payloadForStorage.status;

        const sanitizedPayload = sanitizeForFirebase(finalDbPayload);

        if (Object.keys(sanitizedPayload).length > 0) {
            await updateDoc(messageRef, sanitizedPayload);
        }
        
        state.updateOptimisticMessage(conversationId, messageId, updates);
        return { success: true };

    } catch (error) {
        return { success: false, error: error.message };
    }
  };

  const baseActions = {
    createConversation, updateConversation, updateConversationBotAndModel,
    sendUserMessage, switchToConversation, deleteConversation,
    setupMetaListener: setupMetaListenerInternal, cleanupMetaListener,
    setupMessageListener, cleanupMessageListener,
    addOptimisticAssistantMessage, updateOptimisticAssistantMessage,
    loadMoreMessagesIfNeeded, 
    updateAIMemorySize, updateTemperature,
    deleteFirebaseMessagesWrapper: deleteMessagesFromFirebase,
    formatMessageForStorage,
  };

  const messageActions = createMessageActions(state, baseActions);
  
  return { ...baseActions, ...messageActions, updateMessage };
};